import { useState, useCallback, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Input } from "@/components/ui/input";
import { X, Filter, ChevronDown } from "lucide-react";

const getColorValue = (colorName: string): string => {
  const colorMap: { [key: string]: string } = {
    'durazno': '#FFCBA4',
    'peach': '#FFCBA4',
    'mostaza': '#FFDB58',
    'mustard': '#FFDB58',
    'crema': '#F5F5DC',
    'cream': '#F5F5DC',
    'champagne': '#F7E7CE',
    'rosa': '#FFC0CB',
    'pink': '#FFC0CB',
    'rojo': '#FF0000',
    'red': '#FF0000',
    'blanco': '#FFFFFF',
    'white': '#FFFFFF',
    'amarillo': '#FFFF00',
    'yellow': '#FFFF00',
    'naranja': '#FFA500',
    'orange': '#FFA500',
    'morado': '#800080',
    'purple': '#800080',
    'azul': '#0000FF',
    'blue': '#0000FF',
    'verde': '#008000',
    'green': '#008000',
  };
  
  return colorMap[colorName.toLowerCase()] || colorName.toLowerCase();
};

interface FilterOptions {
  types: string[];
  colors: string[];
  searchTerm?: string;
}

interface FilterState {
  availableTypes: string[];
  availableColors: string[];
}

interface PaginatedFlowerFiltersProps {
  filterOptions: FilterState;
  currentFilters: FilterOptions;
  onFiltersChange: (filters: FilterOptions) => void;
  totalCount: number;
  currentlyRendered: number;
}

export function PaginatedFlowerFilters({ 
  filterOptions, 
  currentFilters, 
  onFiltersChange, 
  totalCount,
  currentlyRendered 
}: PaginatedFlowerFiltersProps) {
  const [isOpen, setIsOpen] = useState(false);

  const handleFiltersChange = useCallback((newFilters: Partial<FilterOptions>) => {
    onFiltersChange({
      ...currentFilters,
      ...newFilters
    });
  }, [currentFilters, onFiltersChange]);

  const toggleType = useCallback((type: string) => {
    const newTypes = currentFilters.types.includes(type) 
      ? currentFilters.types.filter(t => t !== type)
      : [...currentFilters.types, type];
    
    handleFiltersChange({ types: newTypes });
  }, [currentFilters.types, handleFiltersChange]);

  const toggleColor = useCallback((color: string) => {
    const newColors = currentFilters.colors.includes(color) 
      ? currentFilters.colors.filter(c => c !== color)
      : [...currentFilters.colors, color];
    
    handleFiltersChange({ colors: newColors });
  }, [currentFilters.colors, handleFiltersChange]);

  const clearAllFilters = useCallback(() => {
    onFiltersChange({ types: [], colors: [] });
  }, [onFiltersChange]);

  const hasActiveFilters = currentFilters.types.length > 0 || 
                          currentFilters.colors.length > 0;

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <Card className="mb-8 bg-card border-border shadow-soft">
        <CardContent className="px-4 py-1">
          <div 
            className="flex items-center gap-3 mb-2 cursor-pointer -mx-4 px-4 py-1 rounded-t-lg"
            onClick={() => setIsOpen(!isOpen)}
          >
            <Filter className="h-4 w-4 text-primary" />
            <h3 className="text-base font-display font-light text-foreground">
              Filter Flowers
            </h3>
            {hasActiveFilters && (
              <Button
                variant="ghost"
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  clearAllFilters();
                }}
                className="text-muted-foreground hover:text-foreground"
              >
                <X className="h-4 w-4 mr-1" />
                Clear All
              </Button>
            )}
            <ChevronDown className={`h-4 w-4 ml-auto text-muted-foreground transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />
          </div>

          <CollapsibleContent className="space-y-6">

            {/* Flower Type Filter */}
            {filterOptions.availableTypes.length > 0 && (
              <div>
                <h4 className="text-sm font-medium text-foreground mb-3 font-display">Flower Type</h4>
                <div className="flex flex-wrap gap-2">
                  {filterOptions.availableTypes.map(type => (
                    <Badge
                      key={type}
                      variant={currentFilters.types.includes(type) ? "default" : "secondary"}
                      className={`cursor-pointer transition-all duration-200 ${
                        currentFilters.types.includes(type) 
                          ? "bg-primary text-primary-foreground shadow-andean" 
                          : "bg-andean-mist hover:bg-primary/10 hover:text-primary"
                      }`}
                      onClick={() => toggleType(type)}
                    >
                      {type}
                    </Badge>
                  ))}
                </div>
              </div>
            )}

            {/* Color Filter */}
            {filterOptions.availableColors.length > 0 && (
              <div>
                <h4 className="text-sm font-medium text-foreground mb-3 font-display">Color</h4>
                <div className="flex flex-wrap gap-2">
                  {filterOptions.availableColors.map(color => (
                    <Badge
                      key={color}
                      variant={currentFilters.colors.includes(color) ? "default" : "secondary"}
                      className={`cursor-pointer transition-all duration-200 capitalize flex items-center gap-2 ${
                        currentFilters.colors.includes(color) 
                          ? "bg-primary text-primary-foreground shadow-andean" 
                          : "bg-andean-mist hover:bg-primary/10 hover:text-primary"
                      }`}
                      onClick={() => toggleColor(color)}
                    >
                      <div 
                        className="w-3 h-3 rounded-full border border-current/30" 
                        style={{ backgroundColor: getColorValue(color) }}
                      />
                      {color}
                    </Badge>
                  ))}
                </div>
              </div>
            )}

            {hasActiveFilters && (
              <div className="mt-6 pt-4 border-t border-border">
                <p className="text-sm text-muted-foreground font-light">
                  Showing {currentlyRendered} of {totalCount} flowers
                </p>
              </div>
            )}
          </CollapsibleContent>
        </CardContent>
      </Card>
    </Collapsible>
  );
}